import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Plus, Search, Pencil, Trash2, Martini, X, ImagePlus, AlertTriangle,
  ChevronRight, Sparkles, Wine, Calculator, FileText,
} from "lucide-react";
import api, { formatApiError } from "../api";
import {
  Button, Input, Label, Select, Card, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import SmartImportModal from "../components/SmartImportModal";
import CocktailPdfImportModal from "../components/CocktailPdfImportModal";
import IngredientMatchSuggestion from "../components/IngredientMatchSuggestion";
import UnifiedImportButton from "../components/UnifiedImportButton";
import DrinkModal from "../components/DrinkModal";
import { useAuth } from "../contexts/AuthContext";
import {
  formatEUR, formatNumber, COCKTAIL_CATEGORY_LABELS, UNIT_LABELS, ALLERGEN_ABBR,
  ICE_TYPE_LABELS, parseATDecimal,
} from "../utils/format";
import { lineCost, COCKTAIL_LINE_UNITS_FOR } from "../utils/units";

const STATUS_TONES = { active: "ok", inactive: "default", seasonal: "warn" };

function TrafficDot({ light }) {
  const colors = { green: "bg-ok", yellow: "bg-warn", red: "bg-err", neutral: "bg-ink-muted" };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[light] || colors.neutral}`} />;
}

function AllergenChips({ items = [], compact = true }) {
  const { t } = useTranslation();
  if (!items.length) return <span className="text-ink-muted text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((a) => (
        <span key={a} title={t(`allergens.${a}`)}
          className={`inline-flex items-center justify-center rounded border border-warn/30 bg-warn/10 text-warn font-medium ${
            compact ? "px-1 py-0 text-[10px] min-w-[22px] h-[18px]" : "px-1.5 py-0.5 text-[10px]"
          }`}>
          {compact ? ALLERGEN_ABBR[a] || a.slice(0, 2) : t(`allergens.${a}`)}
        </span>
      ))}
    </div>
  );
}

/* ----- Bottle calculator modal ----- */
function BottleCalcModal({ open, onClose }) {
  const { t } = useTranslation();
  const [price, setPrice] = useState("24,90");
  const [volume, setVolume] = useState("700");
  const [unit, setUnit] = useState("ml");

  const result = useMemo(() => {
    const p = parseATDecimal(price);
    const v = parseATDecimal(volume);
    if (!p || !v) return null;
    const factor = unit === "L" ? 1000 : unit === "cl" ? 10 : 1;
    const ml = v * factor;
    if (ml <= 0) return null;
    const perMl = p / ml;
    return { perMl, perCl: perMl * 10, perL: perMl * 1000 };
  }, [price, volume, unit]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="bottle-calc-modal">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm fade-in" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6 fade-in">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wine size={18} className="text-brand" />
              <h2 className="font-display text-lg font-semibold tracking-tight">{t("cocktails.bottle_calc")}</h2>
            </div>
            <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="bottle-calc-close"><X size={16} /></button>
          </div>
          <p className="text-xs text-ink-secondary mb-4">{t("cocktails.bottle_intro")}</p>
          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="col-span-1">
              <Label>{t("cocktails.bottle_price")} (€)</Label>
              <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="bottle-price" />
            </div>
            <div className="col-span-1">
              <Label>{t("cocktails.bottle_volume")}</Label>
              <Input inputMode="decimal" value={volume} onChange={(e) => setVolume(e.target.value)} data-testid="bottle-volume" />
            </div>
            <div className="col-span-1">
              <Label>{t("cocktails.bottle_unit")}</Label>
              <Select value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="bottle-unit">
                <option value="ml">ml</option>
                <option value="cl">cl</option>
                <option value="L">L</option>
              </Select>
            </div>
          </div>
          {result ? (
            <div className="grid grid-cols-3 gap-2">
              <Card className="p-3 border-brand/30 bg-brand/[0.04]">
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.bottle_per_ml")}</div>
                <div className="num text-sm font-medium" data-testid="bottle-per-ml">{formatEUR(result.perMl, 4)}</div>
              </Card>
              <Card className="p-3 border-brand/30 bg-brand/[0.04]">
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.bottle_per_cl")}</div>
                <div className="num text-sm font-medium" data-testid="bottle-per-cl">{formatEUR(result.perCl, 4)}</div>
              </Card>
              <Card className="p-3 border-brand/30 bg-brand/[0.04]">
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.bottle_per_L")}</div>
                <div className="num text-sm font-medium" data-testid="bottle-per-L">{formatEUR(result.perL, 2)}</div>
              </Card>
            </div>
          ) : (
            <div className="text-xs text-ink-muted text-center py-3">—</div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ----- Ingredient picker (mirrors recipes' but allows volume ingredients incl L) ----- */
function IngredientPicker({ ingredients, onPick, exclude = [] }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ingredients
      .filter((i) => !exclude.includes(i.id))
      .filter((i) => !ql || i.name.toLowerCase().includes(ql))
      .slice(0, 8);
  }, [q, ingredients, exclude]);

  return (
    <div data-testid="cocktail-ingredient-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("cocktails.search_ingredient")}
          className="!pl-9"
          data-testid="cocktail-picker-search"
        />
      </div>
      {q && (
        <div className="mt-2 rounded-md border border-white/10 bg-bg-void max-h-64 overflow-y-auto divide-y divide-white/5">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-ink-muted">Keine Treffer.</div>
          ) : (
            filtered.map((i) => (
              <button type="button" key={i.id} onClick={() => { onPick(i); setQ(""); }}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.03] transition-colors"
                data-testid={`cocktail-picker-pick-${i.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-ink-primary truncate">{i.name}</span>
                  <Badge>{i.category}</Badge>
                </div>
                <span className="num text-xs text-brand">
                  {formatEUR(i.price_per_unit, 4)} / {UNIT_LABELS[i.unit] || i.unit}
                </span>
              </button>
            ))
          )}
        </div>
      )}
      {q && filtered.length === 0 && (
        <IngredientMatchSuggestion
          name={q}
          onAccept={(existing) => {
            onPick({
              id: existing.id, name: existing.name, category: existing.category,
              unit: existing.unit, price_per_unit: existing.price_per_unit,
            });
            setQ("");
          }}
        />
      )}
    </div>
  );
}

/* ----- Cocktail slide-over ----- */
function CocktailSlideOver({ open, onClose, mode, initial, meta, ingredients, targetPourCost, onSaved }) {
  const { t } = useTranslation();
  const empty = {
    name: "", category: "Spritz", base_spirit: "", ice_type: "cubed",
    garnishes: [],
    ingredients: [],
    selling_price: "",
    happy_hour_price: "",
    labor_cost_per_drink: "0",
    overhead_per_drink: "0",
    menu_description: "", preparation_method: "",
    photo: "", status: "active",
  };
  const [form, setForm] = useState(empty);
  const [section, setSection] = useState("basics");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        category: initial.category || "Spritz",
        base_spirit: initial.base_spirit || "",
        ice_type: initial.ice_type || "cubed",
        garnishes: (initial.garnishes || []).map((g) => ({ name: g.name, cost: String(g.cost).replace(".", ",") })),
        ingredients: (initial.ingredients || []).map((l) => ({
          ingredient_id: l.ingredient_id, quantity: String(l.quantity).replace(".", ","), unit: l.unit,
        })),
        selling_price: initial.selling_price != null ? String(initial.selling_price).replace(".", ",") : "",
        happy_hour_price: initial.happy_hour_price != null ? String(initial.happy_hour_price).replace(".", ",") : "",
        labor_cost_per_drink: String(initial.labor_cost_per_drink ?? "0").replace(".", ","),
        overhead_per_drink: String(initial.overhead_per_drink ?? "0").replace(".", ","),
        menu_description: initial.menu_description || "",
        preparation_method: initial.preparation_method || "",
        photo: initial.photo || "",
        status: initial.status || "active",
      });
    } else {
      setForm(empty);
    }
    setSection("basics");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial]);

  const ingredientMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const live = useMemo(() => {
    let ingredientCost = 0;
    const allergenSet = new Set();
    const lines = form.ingredients.map((l) => {
      const ing = ingredientMap[l.ingredient_id];
      const qty = parseATDecimal(l.quantity);
      const { cost, incompatible } = lineCost(qty, l.unit, ing);
      ingredientCost += cost;
      if (ing) (ing.allergens || []).forEach((a) => allergenSet.add(a));
      return { ...l, cost, incompatible, ingredient: ing };
    });
    const garnishCost = form.garnishes.reduce((s, g) => s + (parseATDecimal(g.cost) || 0), 0);
    const totalCost = ingredientCost + garnishCost;
    const sell = parseATDecimal(form.selling_price);
    const suggested = targetPourCost > 0 ? totalCost / (targetPourCost / 100) : 0;
    const effective = sell > 0 ? sell : suggested;
    const pcp = effective > 0 ? (totalCost / effective) * 100 : 0;
    const labor = parseATDecimal(form.labor_cost_per_drink);
    const oh = parseATDecimal(form.overhead_per_drink);
    const db1 = effective - totalCost;
    const db2 = db1 - labor - oh;
    let light = "neutral";
    if (pcp > 0) {
      if (pcp <= targetPourCost) light = "green";
      else if (pcp <= targetPourCost + 5) light = "yellow";
      else light = "red";
    }

    // Happy hour live
    const hh = parseATDecimal(form.happy_hour_price);
    let happyHour = null;
    if (hh > 0) {
      const hhPcp = (totalCost / hh) * 100;
      const hhDb1 = hh - totalCost;
      const hhDb2 = hhDb1 - labor - oh;
      happyHour = {
        price: hh, pcp: hhPcp, db1: hhDb1, db2: hhDb2,
        reduction: effective - hh,
        marginLoss: db1 - hhDb1,
      };
    }

    return {
      lines, ingredientCost, garnishCost, totalCost, suggested, effective, pcp,
      db1, db2, light, allergens: Array.from(allergenSet).sort(), happyHour,
    };
  }, [form, ingredientMap, targetPourCost]);

  const addLine = (ing) => {
    const defaultUnit = ing.unit === "L" || ing.unit === "ml" || ing.unit === "cl" ? "cl" : ing.unit;
    setForm((s) => ({ ...s, ingredients: [...s.ingredients, { ingredient_id: ing.id, quantity: "4", unit: defaultUnit }] }));
  };
  const removeLine = (idx) => setForm((s) => ({ ...s, ingredients: s.ingredients.filter((_, i) => i !== idx) }));
  const updateLine = (idx, patch) => setForm((s) => ({
    ...s,
    ingredients: s.ingredients.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const addGarnish = () => setForm((s) => ({ ...s, garnishes: [...s.garnishes, { name: "", cost: "0,30" }] }));
  const updateGarnish = (idx, patch) => setForm((s) => ({
    ...s,
    garnishes: s.garnishes.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
  }));
  const removeGarnish = (idx) => setForm((s) => ({ ...s, garnishes: s.garnishes.filter((_, i) => i !== idx) }));

  const handlePhoto = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("cocktails.photo_too_large")); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const garnishes = form.garnishes
        .filter((g) => g.name.trim())
        .map((g) => ({ name: g.name.trim(), cost: parseATDecimal(g.cost) || 0 }));
      const payload = {
        name: form.name.trim(),
        category: form.category,
        base_spirit: form.base_spirit || "",
        ice_type: form.ice_type || "cubed",
        garnishes,
        ingredients: form.ingredients
          .filter((l) => l.ingredient_id && parseATDecimal(l.quantity) > 0)
          .map((l) => ({ ingredient_id: l.ingredient_id, quantity: parseATDecimal(l.quantity), unit: l.unit })),
        labor_cost_per_drink: parseATDecimal(form.labor_cost_per_drink),
        overhead_per_drink: parseATDecimal(form.overhead_per_drink),
        menu_description: form.menu_description || "",
        preparation_method: form.preparation_method || "",
        photo: form.photo || "",
        status: form.status,
      };
      const sellNum = parseATDecimal(form.selling_price);
      payload.selling_price = sellNum > 0 ? sellNum : null;
      const hhNum = parseATDecimal(form.happy_hour_price);
      payload.happy_hour_price = hhNum > 0 ? hhNum : null;

      if (mode === "edit") {
        await api.patch(`/cocktails/${initial.id}`, payload);
        toast.success("Cocktail aktualisiert.");
      } else {
        await api.post("/cocktails", payload);
        toast.success("Cocktail gespeichert.");
      }
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  const sections = ["basics", "ingredients", "pricing", "presentation"];
  const labels = t("cocktails.sections", { returnObjects: true });

  return (
    <div className="fixed inset-0 z-50" data-testid="cocktail-slideover">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[680px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {mode === "edit" ? "Bearbeiten" : "Neu"}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight">
              {mode === "edit" ? t("cocktails.form_edit") : t("cocktails.form_add")}
            </h2>
          </div>
          <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="cocktail-slide-close"><X size={18} /></button>
        </header>

        <div className="border-b border-white/10 px-6 py-2 flex items-center gap-1 bg-bg-void/40">
          {sections.map((s) => (
            <button key={s} type="button" onClick={() => setSection(s)} data-testid={`cocktail-section-${s}`}
              className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider rounded transition-colors ${
                section === s ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
              }`}>
              {labels[s]}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6" data-testid="cocktail-form">
          {section === "basics" && (
            <div className="space-y-5">
              <div>
                <Label>{t("cocktails.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required data-testid="cocktail-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("cocktails.category")}</Label>
                  <Select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} data-testid="cocktail-form-category">
                    {(meta?.categories || []).map((c) => <option key={c} value={c}>{COCKTAIL_CATEGORY_LABELS[c] || c}</option>)}
                  </Select>
                </div>
                <div>
                  <Label>{t("cocktails.status")}</Label>
                  <Select value={form.status} onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))} data-testid="cocktail-form-status">
                    <option value="active">{t("cocktails.status_active")}</option>
                    <option value="inactive">{t("cocktails.status_inactive")}</option>
                    <option value="seasonal">{t("cocktails.status_seasonal")}</option>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("cocktails.base_spirit")}</Label>
                  <Input value={form.base_spirit} onChange={(e) => setForm((s) => ({ ...s, base_spirit: e.target.value }))} placeholder={t("cocktails.base_spirit_placeholder")} data-testid="cocktail-form-base-spirit" />
                </div>
                <div>
                  <Label>{t("cocktails.ice_type")}</Label>
                  <Select value={form.ice_type} onChange={(e) => setForm((s) => ({ ...s, ice_type: e.target.value }))} data-testid="cocktail-form-ice">
                    {(meta?.ice_types || []).map((i) => <option key={i} value={i}>{ICE_TYPE_LABELS[i] || i}</option>)}
                  </Select>
                </div>
              </div>
            </div>
          )}

          {section === "ingredients" && (
            <div className="space-y-5">
              {ingredients.length === 0 ? (
                <Card className="px-4 py-6 text-center"><p className="text-sm text-ink-secondary">{t("cocktails.no_ingredients_in_db")}</p></Card>
              ) : (
                <IngredientPicker ingredients={ingredients} exclude={form.ingredients.map((l) => l.ingredient_id)} onPick={addLine} />
              )}

              {form.ingredients.length === 0 ? (
                <div className="text-xs text-ink-muted text-center py-4" data-testid="cocktail-no-lines">{t("cocktails.no_ingredients")}</div>
              ) : (
                <Card className="overflow-hidden">
                  <table className="w-full text-sm" data-testid="cocktail-line-table">
                    <thead><tr className="text-left">
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10">{t("cocktails.name")}</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10 text-right">{t("cocktails.qty")}</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10">{t("cocktails.unit")}</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10 text-right bg-brand/[0.04]">{t("cocktails.line_cost")}</th>
                      <th className="px-2 border-b border-white/10" />
                    </tr></thead>
                    <tbody>
                      {live.lines.map((line, idx) => {
                        const ing = line.ingredient;
                        const units = ing ? COCKTAIL_LINE_UNITS_FOR(ing.unit) : ["ml", "cl", "L", "dash", "splash", "Stueck"];
                        return (
                          <tr key={idx} className="border-b border-white/5" data-testid={`cocktail-line-${idx}`}>
                            <td className="px-3 py-2 text-ink-primary">
                              <div className="flex items-center gap-1.5">
                                {ing ? ing.name : <span className="text-err">— gelöscht —</span>}
                                {line.incompatible && <span className="text-warn"><AlertTriangle size={12} /></span>}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <Input inputMode="decimal" value={form.ingredients[idx].quantity}
                                onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                                className="!py-1 !px-2 !text-xs !text-right" data-testid={`cocktail-line-qty-${idx}`} />
                            </td>
                            <td className="px-3 py-2">
                              <Select value={form.ingredients[idx].unit} onChange={(e) => updateLine(idx, { unit: e.target.value })}
                                className="!py-1 !px-2 !text-xs" data-testid={`cocktail-line-unit-${idx}`}>
                                {units.map((u) => <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>)}
                              </Select>
                            </td>
                            <td className="px-3 py-2 num text-right text-ink-primary bg-brand/[0.02]" data-testid={`cocktail-line-cost-${idx}`}>
                              {line.incompatible ? "—" : formatEUR(line.cost, 4)}
                            </td>
                            <td className="px-2 py-2">
                              <button type="button" onClick={() => removeLine(idx)} className="p-1.5 text-ink-secondary hover:text-err transition-colors" data-testid={`cocktail-line-remove-${idx}`} aria-label="Remove"><Trash2 size={13} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              )}

              {/* Garnishes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="!mb-0">{t("cocktails.garnishes")}</Label>
                  <button type="button" onClick={addGarnish} className="text-[11px] text-brand hover:text-brand-hover flex items-center gap-1" data-testid="cocktail-add-garnish">
                    <Plus size={11} /> {t("cocktails.garnish_add")}
                  </button>
                </div>
                {form.garnishes.length === 0 ? (
                  <div className="text-xs text-ink-muted text-center py-3" data-testid="cocktail-no-garnishes">{t("cocktails.no_garnishes")}</div>
                ) : (
                  <div className="space-y-2">
                    {form.garnishes.map((g, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`cocktail-garnish-${idx}`}>
                        <Input value={g.name} onChange={(e) => updateGarnish(idx, { name: e.target.value })} placeholder={t("cocktails.garnish_name")} data-testid={`cocktail-garnish-name-${idx}`} />
                        <Input inputMode="decimal" value={g.cost} onChange={(e) => updateGarnish(idx, { cost: e.target.value })} placeholder="€" data-testid={`cocktail-garnish-cost-${idx}`} />
                        <button type="button" onClick={() => removeGarnish(idx)} className="p-2 text-ink-secondary hover:text-err transition-colors" data-testid={`cocktail-garnish-remove-${idx}`} aria-label="Remove"><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Live cost summary */}
              <div className="grid grid-cols-3 gap-2">
                <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.ingredient_cost")}</div><div className="num text-sm">{formatEUR(live.ingredientCost, 4)}</div></Card>
                <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.garnishes")}</div><div className="num text-sm">{formatEUR(live.garnishCost, 4)}</div></Card>
                <Card className="p-3 bg-brand/[0.04] border-brand/30" data-testid="cocktail-form-total"><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.total_cost")}</div><div className="num text-sm font-medium text-brand">{formatEUR(live.totalCost)}</div></Card>
              </div>

              <div>
                <Label>{t("common.allergens")}</Label>
                <AllergenChips items={live.allergens} compact={false} />
              </div>
            </div>
          )}

          {section === "pricing" && (
            <div className="space-y-5">
              <Card className="p-5 bg-brand/[0.04] border-brand/30">
                <div className="grid grid-cols-3 gap-3">
                  <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.total_cost")}</div><div className="num text-base font-medium">{formatEUR(live.totalCost, 4)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.suggested_price")}</div><div className="num text-base font-medium">{formatEUR(live.suggested)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.target_pour_cost")}</div><div className="num text-base font-medium">{formatNumber(targetPourCost, 1)} %</div></div>
                </div>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("cocktails.selling_price")} (€)</Label>
                  <Input inputMode="decimal" placeholder={t("cocktails.selling_price_auto")} value={form.selling_price} onChange={(e) => setForm((s) => ({ ...s, selling_price: e.target.value }))} data-testid="cocktail-form-selling-price" />
                </div>
                <div className="flex items-end">
                  <div className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.pour_cost_pct")}</div>
                    <div className="flex items-center gap-2"><TrafficDot light={live.light} /><span className="num text-sm font-medium" data-testid="cocktail-form-pcp">{live.pcp ? `${formatNumber(live.pcp, 1)} %` : "—"}</span></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("cocktails.labor")} (€)</Label><Input inputMode="decimal" value={form.labor_cost_per_drink} onChange={(e) => setForm((s) => ({ ...s, labor_cost_per_drink: e.target.value }))} data-testid="cocktail-form-labor" /></div>
                <div><Label>{t("cocktails.overhead")} (€)</Label><Input inputMode="decimal" value={form.overhead_per_drink} onChange={(e) => setForm((s) => ({ ...s, overhead_per_drink: e.target.value }))} data-testid="cocktail-form-overhead" />
                  {(!form.overhead_per_drink || parseATDecimal(form.overhead_per_drink) === 0) && (
                    <div className="text-[10px] text-brand mt-1 flex items-center gap-1" data-testid="cocktail-form-overhead-auto-hint">
                      <span className="inline-block w-1 h-1 rounded-full bg-brand" />
                      {t("cocktails.overhead_auto_hint", "Wenn leer: Aus Betriebskosten (automatisch)")}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.db1")}</div><div className="num text-lg font-medium" data-testid="cocktail-form-db1">{formatEUR(live.db1)}</div></Card>
                <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.db2")}</div><div className={`num text-lg font-medium ${live.db2 < 0 ? "text-err" : ""}`} data-testid="cocktail-form-db2">{formatEUR(live.db2)}</div></Card>
              </div>

              {/* Happy Hour */}
              <Card className="p-4 border-warn/30">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-warn" />
                  <span className="text-[11px] uppercase tracking-wider text-warn font-medium">{t("cocktails.happy_hour")}</span>
                </div>
                <p className="text-xs text-ink-secondary mb-3">{t("cocktails.happy_hour_intro")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("cocktails.happy_hour_price")} (€)</Label>
                    <Input inputMode="decimal" value={form.happy_hour_price} onChange={(e) => setForm((s) => ({ ...s, happy_hour_price: e.target.value }))} data-testid="cocktail-form-hh-price" />
                  </div>
                  {live.happyHour ? (
                    <div className="bg-bg-void border border-white/10 rounded-md px-3 py-2 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-ink-secondary">{t("cocktails.pour_cost_pct")}</span>
                        <span className="num">{formatNumber(live.happyHour.pcp, 1)} %</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-ink-secondary">{t("cocktails.happy_hour_db1")}</span>
                        <span className="num" data-testid="cocktail-form-hh-db1">{formatEUR(live.happyHour.db1)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-ink-secondary">{t("cocktails.happy_hour_loss")}</span>
                        <span className={`num ${live.happyHour.marginLoss > 0 ? "text-err" : "text-ok"}`} data-testid="cocktail-form-hh-loss">{formatEUR(live.happyHour.marginLoss)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-bg-void border border-dashed border-white/10 rounded-md px-3 py-2 text-xs text-ink-muted text-center self-end">—</div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {section === "presentation" && (
            <div className="space-y-5">
              <div>
                <Label>{t("cocktails.menu_description")}</Label>
                <textarea rows={3} value={form.menu_description} onChange={(e) => setForm((s) => ({ ...s, menu_description: e.target.value }))} className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all resize-none" data-testid="cocktail-form-menu-desc" />
              </div>
              <div>
                <Label>{t("cocktails.preparation")}</Label>
                <textarea rows={5} value={form.preparation_method} onChange={(e) => setForm((s) => ({ ...s, preparation_method: e.target.value }))} className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all resize-none" data-testid="cocktail-form-prep" />
              </div>
              <div>
                <Label>{t("cocktails.photo")}</Label>
                {form.photo ? (
                  <div className="relative w-full h-48 rounded-md overflow-hidden border border-white/10 bg-bg-void">
                    <img src={form.photo} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setForm((s) => ({ ...s, photo: "" }))} className="absolute top-2 right-2 px-2.5 py-1 rounded bg-bg-void/90 border border-white/10 text-xs text-ink-secondary hover:text-err" data-testid="cocktail-form-photo-remove">{t("cocktails.photo_remove")}</button>
                  </div>
                ) : (
                  <label className="w-full h-32 border border-dashed border-white/15 rounded-md flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-brand/40 hover:bg-brand/[0.03] transition-colors" data-testid="cocktail-form-photo-pick">
                    <ImagePlus size={20} className="text-ink-secondary" />
                    <span className="text-xs text-ink-secondary">{t("cocktails.photo_pick")}</span>
                    <input type="file" accept="image/*" onChange={(e) => handlePhoto(e.target.files?.[0])} className="hidden" data-testid="cocktail-form-photo-input" />
                  </label>
                )}
              </div>
            </div>
          )}
        </form>

        <footer className="border-t border-white/10 px-4 py-3 flex items-center justify-between bg-bg-void/40">
          <div className="text-xs text-ink-muted">
            {section !== "basics" && (<button type="button" onClick={() => setSection(sections[sections.indexOf(section) - 1])} className="hover:text-ink-primary">← {labels[sections[sections.indexOf(section) - 1]]}</button>)}
          </div>
          <div className="flex items-center gap-2">
            {section !== "presentation" && <Button type="button" onClick={() => setSection(sections[sections.indexOf(section) + 1])} data-testid="cocktail-form-next">{labels[sections[sections.indexOf(section) + 1]]} →</Button>}
            <Button variant="secondary" type="button" onClick={onClose} data-testid="cocktail-form-cancel">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={saving} data-testid="cocktail-form-save">{saving ? t("common.loading") : t("common.save")}</Button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

/* ----- Cocktail detail panel with live happy-hour recalculator ----- */
function CocktailDetail({ cocktail, onClose, onReload }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasDrinkModule = (user?.modules || []).includes("drink");
  const [drinkOpen, setDrinkOpen] = useState(false);
  const [hh, setHH] = useState("");
  useEffect(() => {
    if (cocktail?.happy_hour_price != null) setHH(String(cocktail.happy_hour_price).replace(".", ","));
    else setHH("");
  }, [cocktail]);

  const hhImpact = useMemo(() => {
    if (!cocktail) return null;
    const price = parseATDecimal(hh);
    if (!price || price <= 0) return null;
    const totalCost = cocktail.totals?.total_cost || 0;
    const labor = cocktail.labor_cost_per_drink || 0;
    const oh = cocktail.overhead_per_drink || 0;
    const pcp = (totalCost / price) * 100;
    const db1 = price - totalCost;
    const db2 = db1 - labor - oh;
    const eff = cocktail.totals?.effective_selling_price || 0;
    return { pcp, db1, db2, marginLoss: (cocktail.totals?.db1 || 0) - db1, reduction: eff - price };
  }, [cocktail, hh]);

  if (!cocktail) return null;
  const totals = cocktail.totals || {};

  return (
    <div className="fixed inset-0 z-50" data-testid="cocktail-detail">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[600px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {COCKTAIL_CATEGORY_LABELS[cocktail.category] || cocktail.category}
              {cocktail.base_spirit ? ` · ${cocktail.base_spirit}` : ""}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight truncate">{cocktail.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            {hasDrinkModule && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDrinkOpen(true)}
                data-testid="cocktail-detail-drink-btn"
                title="Drink Intelligence"
              >
                <Martini size={13} />
                <span className="hidden sm:inline">Drink Intelligence</span>
              </Button>
            )}
            <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="cocktail-detail-close"><X size={18} /></button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {cocktail.photo && <img src={cocktail.photo} alt={cocktail.name} className="w-full h-44 object-cover rounded-md border border-white/10" />}

          <Card className="p-5 bg-brand/[0.04] border-brand/30">
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.total_cost")}</div><div className="num text-lg font-medium">{formatEUR(totals.total_cost, 4)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.selling_price")}</div><div className="num text-lg font-medium">{formatEUR(totals.effective_selling_price)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.pour_cost_pct")}</div><div className="flex items-center gap-2 num text-lg font-medium"><TrafficDot light={totals.traffic_light} />{formatNumber(totals.pour_cost_pct, 1)} %</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("cocktails.target_pour_cost")}</div><div className="num text-lg font-medium">{formatNumber(totals.target_pour_cost, 1)} %</div></div>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.db1")}</div><div className="num text-base font-medium">{formatEUR(totals.db1)}</div></Card>
            <Card className="p-4"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.db2")}</div><div className={`num text-base font-medium ${totals.db2 < 0 ? "text-err" : ""}`}>{formatEUR(totals.db2)}</div>
              {totals.overhead_source === "business_cost_profile" && totals.overhead_per_drink > 0 && (
                <div className="text-[10px] text-brand mt-1.5 flex items-center gap-1" data-testid="cocktail-db2-auto-badge">
                  <span className="inline-block w-1 h-1 rounded-full bg-brand" />
                  {t("cocktails.db2_auto_overhead", "Overhead {{val}} · Aus Betriebskosten (automatisch)", { val: formatEUR(totals.overhead_per_drink) })}
                </div>
              )}
            </Card>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("cocktails.ingredients")}</div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {(cocktail.ingredients || []).map((l, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="px-3 py-2 text-ink-primary">{l.ingredient_name}{l.incompatible_unit && <span className="ml-1.5 text-warn"><AlertTriangle size={11} className="inline" /></span>}</td>
                      <td className="px-3 py-2 num text-ink-secondary text-right text-xs">{l.quantity} {UNIT_LABELS[l.unit] || l.unit}</td>
                      <td className="px-3 py-2 num text-right">{formatEUR(l.line_cost, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {(cocktail.garnishes || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("cocktails.garnishes")}</div>
              <div className="space-y-1">
                {cocktail.garnishes.map((g, i) => (
                  <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-bg-void rounded-md border border-white/5">
                    <span className="text-ink-primary">{g.name}</span>
                    <span className="num text-ink-secondary">{formatEUR(g.cost)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Happy hour recalculator */}
          <Card className="p-4 border-warn/30">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-warn" />
              <span className="text-[11px] uppercase tracking-wider text-warn font-medium">{t("cocktails.happy_hour_calc")}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label>{t("cocktails.happy_hour_price")} (€)</Label>
                <Input inputMode="decimal" value={hh} onChange={(e) => setHH(e.target.value)} data-testid="cocktail-detail-hh-price" />
              </div>
            </div>
            {hhImpact ? (
              <div className="grid grid-cols-3 gap-2">
                <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.pour_cost_pct")}</div><div className="num text-sm" data-testid="cocktail-detail-hh-pcp">{formatNumber(hhImpact.pcp, 1)} %</div></Card>
                <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.happy_hour_db1")}</div><div className="num text-sm" data-testid="cocktail-detail-hh-db1">{formatEUR(hhImpact.db1)}</div></Card>
                <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("cocktails.happy_hour_loss")}</div><div className={`num text-sm ${hhImpact.marginLoss > 0 ? "text-err" : "text-ok"}`} data-testid="cocktail-detail-hh-loss">{formatEUR(hhImpact.marginLoss)}</div></Card>
              </div>
            ) : <div className="text-xs text-ink-muted text-center py-3">—</div>}
          </Card>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("common.allergens")}</div>
            <AllergenChips items={cocktail.allergens} compact={false} />
          </div>

          {cocktail.menu_description && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("cocktails.menu_description")}</div>
              <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{cocktail.menu_description}</p>
            </div>
          )}
          {cocktail.preparation_method && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("cocktails.preparation")}</div>
              <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{cocktail.preparation_method}</p>
            </div>
          )}
        </div>
      </aside>
      {drinkOpen && (
        <DrinkModal cocktail={cocktail} onClose={() => setDrinkOpen(false)} />
      )}
    </div>
  );
}

/* ----- main page ----- */
export default function CocktailsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasDrinkModule = (user?.modules || []).includes("drink");
  const [drinkFor, setDrinkFor] = useState(null);
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [targetPourCost, setTargetPourCost] = useState(20);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [slideOpen, setSlideOpen] = useState(false);
  const [slideMode, setSlideMode] = useState("add");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bottleOpen, setBottleOpen] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const loadInit = async () => {
    try {
      const [m, ing] = await Promise.all([api.get("/cocktails/meta"), api.get("/ingredients")]);
      setMeta(m.data);
      setTargetPourCost(Number(m.data.target_pour_cost) || 20);
      setIngredients(ing.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get("/cocktails", { params });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadInit(); }, []);
  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, status]);

  const onDelete = async (id) => {
    if (!window.confirm(t("cocktails.delete_confirm"))) return;
    try { await api.delete(`/cocktails/${id}`); toast.success("Gelöscht."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openAdd = () => { setSlideMode("add"); setEditing(null); setSlideOpen(true); };
  const openEdit = async (r) => {
    try { const { data } = await api.get(`/cocktails/${r.id}`); setSlideMode("edit"); setEditing(data); setSlideOpen(true); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openDetail = async (r) => {
    try { const { data } = await api.get(`/cocktails/${r.id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 30 — Excel export
  const exportXlsx = async () => {
    try {
      const res = await api.get("/cocktails/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const ddmmyyyy = `${String(today.getDate()).padStart(2, "0")}${String(today.getMonth() + 1).padStart(2, "0")}${today.getFullYear()}`;
      a.download = `StudioOps_Cocktails_${ddmmyyyy}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="cocktails-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">Modul · 03</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Martini size={28} className="text-brand" /> {t("cocktails.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{t("cocktails.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="brand"><Sparkles size={11} /> {t("cocktails.target_pour_cost")} {formatNumber(targetPourCost, 1)} %</Badge>
          <Button variant="secondary" onClick={() => setBottleOpen(true)} data-testid="open-bottle-calc"><Calculator size={14} /> {t("cocktails.bottle_calc_short", "Bottle")}</Button>
          <Button variant="secondary" onClick={() => setSmartOpen(true)} data-testid="cocktails-smart-import"><Sparkles size={14} /> {t("imports.smart_button_short", "Smart Import")}</Button>
          <UnifiedImportButton
            acceptCsvXlsx={false}
            acceptPdf
            onPdfFile={() => setPdfOpen(true)}
            testId="cocktails-pdf-import"
            label={t("cocktails.menu_pdf_short", "Karte-PDF")}
          />
          <Button variant="secondary" onClick={exportXlsx} data-testid="cocktails-export-btn"><FileText size={14} /> {t("cocktails.export", "Excel-Export")}</Button>
          <Button onClick={openAdd} data-testid="add-cocktail-button"><Plus size={14} /> {t("cocktails.add")}</Button>
        </div>
      </div>

      <Card className="px-3 py-2.5 mb-4 flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("cocktails.search_placeholder")} className="!pl-9 !border-transparent !bg-transparent" data-testid="cocktails-search" />
        </div>
        <div className="hidden md:block w-px h-6 bg-white/10" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="md:max-w-[200px]" data-testid="cocktails-category-filter">
          <option value="">{t("cocktails.all_categories")}</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{COCKTAIL_CATEGORY_LABELS[c] || c}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="md:max-w-[180px]" data-testid="cocktails-status-filter">
          <option value="">{t("cocktails.all_statuses")}</option>
          <option value="active">{t("cocktails.status_active")}</option>
          <option value="inactive">{t("cocktails.status_inactive")}</option>
          <option value="seasonal">{t("cocktails.status_seasonal")}</option>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center"><Spinner size={20} /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={Martini} title={t("cocktails.empty_title")} description={t("cocktails.empty_subtitle")}
            action={<Button onClick={openAdd} data-testid="empty-add-cocktail"><Plus size={14} /> {t("cocktails.add")}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cocktails-table">
              <thead><tr className="text-left">
                {[t("cocktails.name"), t("cocktails.category"), t("cocktails.base_spirit"),
                  t("cocktails.total_cost"), t("cocktails.selling_price"),
                  t("cocktails.pour_cost_pct"), t("cocktails.db1"),
                  t("common.allergens"), t("cocktails.status"), ""].map((h, i) => (
                  <th key={i} className={`text-[10px] uppercase tracking-[0.05em] font-medium text-ink-secondary px-4 py-3 border-b border-white/10 ${[3, 4, 5, 6].includes(i) ? "text-right" : ""} ${i === 5 ? "bg-brand/[0.04]" : ""}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr key={r.id} onClick={() => openDetail(r)} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer group" data-testid={`cocktail-row-${idx}`}>
                    <td className="px-4 py-2.5 text-ink-primary font-medium">{r.name}</td>
                    <td className="px-4 py-2.5"><Badge>{COCKTAIL_CATEGORY_LABELS[r.category] || r.category}</Badge></td>
                    <td className="px-4 py-2.5 text-ink-secondary text-xs">{r.base_spirit || "—"}</td>
                    <td className="px-4 py-2.5 num text-right">{formatEUR(r.totals?.total_cost)}</td>
                    <td className="px-4 py-2.5 num text-right">{formatEUR(r.totals?.effective_selling_price)}</td>
                    <td className="px-4 py-2.5 num text-right bg-brand/[0.03]" data-testid={`cocktail-row-pcp-${idx}`}>
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <TrafficDot light={r.totals?.traffic_light} />
                        {r.totals?.pour_cost_pct ? `${formatNumber(r.totals.pour_cost_pct, 1)} %` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 num text-right">{formatEUR(r.totals?.db1)}</td>
                    <td className="px-4 py-2.5"><AllergenChips items={r.allergens} compact /></td>
                    <td className="px-4 py-2.5"><Badge tone={STATUS_TONES[r.status] || "default"}>{r.status === "active" ? t("cocktails.status_active") : r.status === "inactive" ? t("cocktails.status_inactive") : t("cocktails.status_seasonal")}</Badge></td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        {hasDrinkModule && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDrinkFor(r); }}
                            className="p-1.5 rounded text-ink-secondary hover:text-brand hover:bg-brand/10 transition-colors"
                            data-testid={`cocktail-drink-${idx}`}
                            aria-label="Drink Intelligence"
                            title="Drink Intelligence"
                          >
                            <Martini size={13} />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1.5 rounded text-ink-secondary hover:text-brand hover:bg-brand/10 transition-colors" data-testid={`cocktail-edit-${idx}`} aria-label="Edit"><Pencil size={13} /></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} className="p-1.5 rounded text-ink-secondary hover:text-err hover:bg-err/10 transition-colors" data-testid={`cocktail-delete-${idx}`} aria-label="Delete"><Trash2 size={13} /></button>
                        <ChevronRight size={13} className="text-ink-muted ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <CocktailSlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        mode={slideMode}
        initial={editing}
        meta={meta}
        ingredients={ingredients}
        targetPourCost={targetPourCost}
        onSaved={load}
      />
      <CocktailDetail cocktail={detail} onClose={() => setDetail(null)} onReload={load} />
      <BottleCalcModal open={bottleOpen} onClose={() => setBottleOpen(false)} />
      {smartOpen && (
        <SmartImportModal
          kind="bar"
          onClose={() => setSmartOpen(false)}
          onDone={() => { setSmartOpen(false); load(); }}
        />
      )}
      {pdfOpen && (
        <CocktailPdfImportModal
          onClose={() => setPdfOpen(false)}
          onDone={() => { setPdfOpen(false); load(); }}
        />
      )}
      {drinkFor && (
        <DrinkModal cocktail={drinkFor} onClose={() => setDrinkFor(null)} />
      )}
    </div>
  );
}
